import type { WikiItem, WikiItemPortEquippedItem, WikiVehiclePort } from "~~/types/gamedata";
import type { ComponentRowData, ShipCompareShip } from "~~/types/shipexkurs";
import type { ShipexkursIconName } from "~/utils/shipexkurs-icons";
import type { ComponentGroup } from "~/utils/get-component-group";

/** Interne SC-Bezeichner wie `lfsp_filter_subitem_standard` statt eines echten Produktnamens. */
const TECHNICAL_NAME = /^[a-z0-9]+(_[a-z0-9]+)+$/;
const PLACEHOLDER_NAMES = new Set(["<= placeholder =>", "undefined", "unknown"]);

function isUsableName(value: string | null | undefined): value is string {
  if (!value) return false;
  const name = value.trim();
  return !!name && !PLACEHOLDER_NAMES.has(name.toLowerCase()) && !TECHNICAL_NAME.test(name);
}

/**
 * Viele strukturelle Teile (Triebwerke, Treibstoffeinlässe, ...) sind in der
 * API namenlos ("<= PLACEHOLDER =>") oder tragen nur einen internen
 * snake_case-Bezeichner — sie haben schlicht kein Produkt mit Markennamen,
 * nur Kennzahlen. FleetYards zeigt dafür generische Labels wie "Fix"; wir
 * nehmen `type_label`/`sub_type_label`, wenn vorhanden.
 */
function resolveName(equipped: WikiItemPortEquippedItem, shipLabel: string | undefined): string | null {
  // Frachtraster tragen keinen Produktnamen, sondern nur die generische
  // Klassifikation "Cargo Grid" — FleetYards zeigt stattdessen z.B.
  // "Orig 100i Cargogrid" (Hersteller-Code + Schiffsname).
  if (equipped.type === "CargoGrid" && shipLabel) return `${shipLabel} Cargogrid`;

  for (const candidate of [equipped.name, equipped.sub_type_label, equipped.type_label]) {
    if (isUsableName(candidate)) return candidate.trim();
  }

  return equipped.type ?? null;
}

/** Gemeinsame Form für verschachtelte Ports, egal ob Vehicle- oder Item-Ebene (siehe `buildRow`). */
interface NestedPortLike {
  name?: string | null;
  sizes?: { min?: number | null; max?: number | null } | null;
  compatible_types?: Array<{ type: string; sub_types?: string[] | null }> | null;
  equipped_item?: WikiItemPortEquippedItem | null;
  ports?: NestedPortLike[] | null;
}

/** Unbestückte Mining-Modulslots gehören sichtbar zum Mining-Laser. */
function buildEmptyMiningModuleRow(port: NestedPortLike): ComponentRowData | null {
  if (!port.compatible_types?.some((entry) => entry.type === "MiningModifier")) return null;

  const size = port.sizes?.max ?? port.sizes?.min ?? null;
  return {
    key: `empty-mining-module|${size ?? ""}`,
    name: "Leer",
    manufacturer: null,
    manufacturerLogo: null,
    size,
    class: null,
    grade: null,
    type: "MiningModifier",
    subType: null,
    headline: null,
    details: [],
    badges: [],
    children: [],
    count: 1,
  };
}

/** Erkannte Systemkomponenten sind auch ohne eigene Kennzahlen relevante Ausstattung. */
function isRecognizedComponentRow(row: ComponentRowData): boolean {
  return !!getShipexkursComponentIcon(row.type, row.subType);
}

/**
 * Baut eine Zeile aus einem Port. `nestedPorts` sind bereits am *Vehicle*-Port
 * verschachtelte Sub-Ports (z.B. der Jump-Drive-Port unter einem
 * Quantenantrieb, `port.ports`) — die kommen zusätzlich zu den Sub-Ports des
 * separat nachgeladenen Items (`item.ports`, z.B. Frachtraster in einem
 * Cargo-Modul), da beide unterschiedliche Verschachtelungsquellen der API sind.
 */
function buildRow(
  equipped: WikiItemPortEquippedItem,
  items: Map<string, WikiItem>,
  shipLabel: string | undefined,
  nestedPorts: NestedPortLike[] = [],
): ComponentRowData | null {
  const name = resolveName(equipped, shipLabel);
  if (!name) return null;

  const item = equipped.uuid ? items.get(equipped.uuid) : undefined;
  // Die Wiki-API embedded die Stats oft schon direkt am Port-`equipped_item`
  // (verifiziert bei Triebwerken, Quanten-/Sprungantrieben) — `item` als
  // Ergänzung/Rückfall, nicht als einzige Quelle.
  const source = item ?? equipped;
  const { headline, details, badges } = getComponentStats(source);

  // Vehicle-embedded Nesting hat Vorrang: manche Items (z.B. ComfortAir)
  // spiegeln ihr Standard-Loadout auch in `item.ports` (Katalog), was sich
  // sonst mit `nestedPorts` (Vehicle-Ebene) doppelt — nur auf `item.ports`
  // zurückfallen, wenn die Vehicle-Ebene selbst keine Sub-Ports kennt (z.B.
  // Frachtraster in einem Cargo-Modul).
  const childPorts = nestedPorts.length ? nestedPorts : (item?.ports ?? []);
  const children = childPorts
    .map((port) => (
      port.equipped_item
        ? buildRow(port.equipped_item, items, shipLabel, port.ports ?? [])
        : buildEmptyMiningModuleRow(port)
    ))
    .filter((row): row is ComponentRowData => !!row)
    // Sub-Komponenten ohne Kennzahlen und ohne eigene Kinder tragen keine
    // Information (z.B. ein interner Filter-Slot ohne Produktdaten) — nicht
    // als leere Zeile anzeigen.
    .filter((row) => row.headline || row.details.length > 0 || row.children.length > 0 || isRecognizedComponentRow(row));

  const type = equipped.type ?? item?.type ?? null;

  // Reine Gimbal-/Werkzeug-Halterungen (`UtilityTurret`, z.B. "mount_gimbal_mining"
  // bei Mining-Armen) tragen selbst nie eigene Kennzahlen und nur einen
  // technischen Namen — sie fügen dem Baum nur eine leere Zwischenzeile
  // zwischen Arm und eigentlichem Werkzeug hinzu. Bei genau einem Kind wird
  // die Halterung übersprungen, das Kind rückt direkt unter den Arm.
  if (type === "UtilityTurret" && !headline && details.length === 0 && children.length === 1) {
    return children[0]!;
  }

  const itemClass = equipped.class ?? item?.class ?? null;
  const grade = equipped.grade ?? item?.grade ?? null;
  const subType = equipped.sub_type ?? item?.sub_type ?? null;
  // "Unknown" ist ein API-Platzhalter für nicht gesetzte Hersteller (v.a. bei
  // strukturellen Teilen ohne Markenprodukt), kein echter Herstellername.
  const manufacturerName = source.manufacturer?.name ?? null;
  const manufacturer = manufacturerName && manufacturerName.toLowerCase() !== "unknown" ? manufacturerName : null;
  const manufacturerLogo = manufacturer ? (source.manufacturer?.logo ?? null) : null;
  const size = source.size ?? null;

  const detailsKey = details.map((d) => `${d.label}:${d.value}`).join(",");
  return {
    key: `${manufacturer ?? ""}|${size ?? ""}|${itemClass ?? ""}|${grade ?? ""}|${headline?.value ?? ""}|${headline?.unit ?? ""}|${detailsKey}`,
    name,
    manufacturer,
    manufacturerLogo,
    size,
    class: itemClass,
    grade,
    type,
    subType,
    headline,
    details,
    badges: badges ?? [],
    children,
    count: 1,
  };
}

export interface ComponentSubGroup {
  label: string;
  icon: ShipexkursIconName;
  rows: ComponentRowData[];
}

export interface ComponentSuperGroup {
  label: string;
  subGroups: ComponentSubGroup[];
}

/** Computer-Ports besitzen in der API noch kein Item; angezeigt wird nur ihre Anzahl. */
function buildComputerRow(): ComponentRowData {
  return {
    key: "computer-port",
    name: "Computer",
    manufacturer: null,
    manufacturerLogo: null,
    size: null,
    class: null,
    grade: null,
    type: "Computer",
    subType: null,
    headline: null,
    details: [],
    badges: [],
    children: [],
    count: 1,
  };
}

/**
 * Läuft den Port-Baum ab und sammelt alle Ports, die `getComponentGroup()`
 * erkennt — inklusive solcher, die unter einem namenlosen Strukturport
 * verschachtelt sind (z.B. Hull C: Kühler, zweites Power Plant und
 * Quantenantrieb hängen alle unter `hardpoint_body_int_rear`, einem
 * "Room"-Port ohne eigene `category_label`, verifiziert gegen die Live-API).
 * Wird ein Port erkannt, wird NICHT weiter in seine eigenen `ports`
 * rekursiert — die zeigt `buildRow()` bereits selbst als Kind-Zeilen an
 * (z.B. ein Jump-Drive-Sub-Port unter einem Quantenantrieb).
 */
function collectGroupedPorts(
  ports: WikiVehiclePort[],
  items: Map<string, WikiItem>,
): Array<{ port: WikiVehiclePort; group: ComponentGroup }> {
  const result: Array<{ port: WikiVehiclePort; group: ComponentGroup }> = [];

  for (const port of ports) {
    const item = port.equipped_item?.uuid ? items.get(port.equipped_item.uuid) : undefined;
    const group = getComponentGroup(port, item);
    if (group) {
      result.push({ port, group });
      continue;
    }
    if (port.ports?.length) {
      result.push(...collectGroupedPorts(port.ports, items));
    }
  }

  return result;
}

/**
 * Baut die zweistufige Gruppierung (Super-Kategorie → Unter-Abschnitt →
 * Zeilen) aus den Vehicle-Ports eines Schiffs. Gemeinsame Grundlage für den
 * Einzelschiff-Komponentenbaum (`ShipexkursComponentTree`) und den
 * Ausrüstungsvergleich auf der Vergleichsseite.
 */
export function buildComponentSuperGroups(
  ports: WikiVehiclePort[],
  items: Map<string, WikiItem>,
  shipLabel?: string,
): ComponentSuperGroup[] {
  const bySuper = new Map<string, Map<string, { icon: ShipexkursIconName; rows: ComponentRowData[] }>>();

  // Absichtlich NICHT nach `editable` gefiltert: Triebwerke, Treibstoff und
  // Panzerung sind zwar `editable: false` (im Spiel nicht tauschbar), aber
  // trotzdem relevante Kennzahlen für die Anzeige (siehe FleetYards). Was
  // gezeigt wird, steuert stattdessen allein die Kategorie-Allowlist in
  // `getComponentGroup()`.
  for (const { port, group } of collectGroupedPorts(ports, items)) {
    const row = port.equipped_item
      ? buildRow(port.equipped_item, items, shipLabel, port.ports ?? [])
      : isComputerPort(port)
        ? buildComputerRow()
        : null;
    if (!row) continue;
    const { superGroup, subGroup, icon } = group;

    const subMap = bySuper.get(superGroup) ?? new Map();
    const entry = subMap.get(subGroup) ?? { icon, rows: [] };
    entry.rows.push(row);
    subMap.set(subGroup, entry);
    bySuper.set(superGroup, subMap);
  }

  return [...bySuper.entries()].map(([label, subMap]) => ({
    label,
    subGroups: [...subMap.entries()].map(([subLabel, { icon, rows }]) => ({
      label: subLabel,
      icon,
      rows: mergeComponentRows(rows),
    })),
  }));
}

/** Anker-ID einer Ausrüstungs-Kategorie — von `vergleich.vue` (Inhaltsverzeichnis) und `component-comparison.vue` (Karten-`id`) geteilt, damit beide garantiert übereinstimmen. */
export function getComponentCategoryAnchorId(superGroupLabel: string): string {
  const slug = superGroupLabel
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `ausruestung-${slug}`;
}

export interface ComponentCategoryRef {
  id: string;
  label: string;
}

/**
 * Nur die im Vergleich vorkommenden Ausrüstungs-Kategorien (superGroup-Labels),
 * in der kuratierten `COMPONENT_COLUMNS`-Reihenfolge — für das Inhaltsverzeichnis
 * der Vergleichsseite. Deutlich günstiger als `buildComponentSuperGroups()`,
 * weil hier keine Zeilen (Namen, Kennzahlen, Merge, ...) gebaut werden, nur die
 * Kategorie-Zuordnung pro Port geprüft wird.
 */
export function getPresentComponentCategories(ships: ShipCompareShip[]): ComponentCategoryRef[] {
  const present = new Set<string>();

  for (const ship of ships) {
    for (const { group } of collectGroupedPorts(ship.raw?.ports ?? [], ship.items)) {
      present.add(group.superGroup);
    }
  }

  return COMPONENT_COLUMNS.flat()
    .filter((label) => present.has(label))
    .map((label) => ({ id: getComponentCategoryAnchorId(label), label }));
}
